"use client";

/**
 * Records WHERE a signup was launched from — which surface, what action
 * triggered it, and which asset — so PostHog can pin it as a `$set_once` person
 * property at identify time. This is how we answer "most signups come from
 * trying to download while logged out": the express dialog stamps the surface
 * here at signup, and `identity.ts` reads it once the new person is identified
 * (which happens after profile load, not at the signup moment).
 *
 * localStorage, best-effort, 1-hour TTL so it survives the email-verification
 * round-trip (sign up → open inbox → click link → return identified).
 */
import type { SignupSurface, SignupTrigger } from "./events";

const KEY = "uefn-signup-surface";
const TTL_MS = 60 * 60 * 1000;

export interface SignupSurfaceRecord {
  surface: SignupSurface;
  trigger?: SignupTrigger;
  asset_id?: string;
  ts: number;
}

/** Stamp the acquisition surface at signup time (call once, when the user submits). */
export function recordSignupSurface(rec: Omit<SignupSurfaceRecord, "ts">): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...rec, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

/** Read the stamped surface (within TTL). Does NOT clear it — identity sync may
 *  run several times; clear explicitly via {@link clearSignupSurface} once pinned. */
export function getSignupSurface(): SignupSurfaceRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SignupSurfaceRecord;
    if (!parsed?.ts || Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearSignupSurface(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
